import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import * as Icons from "lucide-react";
import { FREEAYTO_LOGO, NAV_ITEMS } from "../data/mock";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [desktopToolsOpen, setDesktopToolsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMobileToolsOpen(false);
    setDesktopToolsOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  const isActive = (to) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  };
  const isToolsActive = location.pathname.startsWith("/tools");

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/80 border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-24 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 group shrink-0">
          <img
            src={FREEAYTO_LOGO}
            alt="FREEAYTO"
            className="h-16 md:h-20 w-auto transition-transform duration-300 group-hover:scale-[1.03]"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_ITEMS.map((item) => {
            if (item.dropdown) {
              return (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => setDesktopToolsOpen(true)}
                  onMouseLeave={() => setDesktopToolsOpen(false)}
                >
                  <button
                    className={`flex items-center gap-1.5 text-[15px] font-medium tracking-tight transition-colors duration-200 ${
                      isToolsActive
                        ? "bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 bg-clip-text text-transparent"
                        : "text-neutral-700 hover:text-neutral-950"
                    }`}
                  >
                    {item.label}
                    <ChevronDown
                      size={14}
                      className={`transition-transform duration-300 ${
                        desktopToolsOpen ? "rotate-180" : ""
                      } ${isToolsActive ? "text-pink-500" : "text-neutral-500"}`}
                    />
                  </button>
                  {desktopToolsOpen && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-[340px]">
                      <div className="bg-white rounded-2xl shadow-[0_30px_60px_-20px_rgba(0,0,0,0.25)] border border-black/5 overflow-hidden p-2">
                        {item.dropdown.map((sub) => {
                          const Ico = Icons[sub.icon] || Icons.Sparkles;
                          return (
                            <Link
                              key={sub.to}
                              to={sub.to}
                              className="flex items-start gap-3 p-3 rounded-xl hover:bg-neutral-50 transition-colors group"
                            >
                              <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 via-orange-500 to-purple-500 flex items-center justify-center">
                                <Ico size={18} className="text-white" />
                              </div>
                              <div className="flex-1">
                                <div className="font-bold text-sm text-neutral-950 group-hover:text-pink-500 transition-colors">
                                  {sub.label}
                                </div>
                                <div className="text-xs text-neutral-500 mt-0.5">
                                  {sub.desc}
                                </div>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`text-[15px] font-medium tracking-tight transition-colors duration-200 ${
                  isActive(item.to)
                    ? "bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 bg-clip-text text-transparent"
                    : "text-neutral-700 hover:text-neutral-950"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Always-visible Jetzt buchen CTA */}
        <Link
          to="/kontakt"
          className="hidden md:inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 text-white text-sm font-bold tracking-tight shadow-[0_8px_24px_-8px_rgba(236,72,153,0.55)] hover:shadow-[0_12px_30px_-8px_rgba(236,72,153,0.7)] hover:-translate-y-[1px] transition-all duration-300"
        >
          Jetzt buchen
          <ArrowRight size={16} />
        </Link>

        <button
          className="lg:hidden p-2 text-neutral-900"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menü"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-black/5">
          <nav className="px-6 py-6 flex flex-col gap-1">
            {NAV_ITEMS.map((item) => {
              if (item.dropdown) {
                return (
                  <div key={item.label} className="py-2">
                    <button
                      onClick={() => setMobileToolsOpen((v) => !v)}
                      className="w-full flex items-center justify-between text-left text-lg font-medium text-neutral-800"
                    >
                      {item.label}
                      <ChevronDown
                        size={18}
                        className={`transition-transform ${mobileToolsOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    {mobileToolsOpen && (
                      <div className="mt-3 ml-2 pl-3 border-l-2 border-pink-200 space-y-3">
                        {item.dropdown.map((sub) => (
                          <Link
                            key={sub.to}
                            to={sub.to}
                            className="block text-base text-neutral-700 hover:text-pink-500 transition-colors"
                          >
                            <div className="font-semibold">{sub.label}</div>
                            <div className="text-xs text-neutral-500">{sub.desc}</div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`py-2 text-lg font-medium transition-colors ${
                    isActive(item.to)
                      ? "bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 bg-clip-text text-transparent"
                      : "text-neutral-800 hover:text-pink-500"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            <Link
              to="/kontakt"
              className="mt-4 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 text-white text-sm font-bold shadow-lg"
            >
              Jetzt buchen
              <ArrowRight size={16} />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
